import { Component } from '@theme/component';
import { debounce, onDocumentLoaded } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

const ACTIVATE_DELAY = 0;
const DEACTIVATE_DELAY = 350;

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  #abortController = new AbortController();
  #featuredSliderUpdateTimeout;

  connectedCallback() {
    super.connectedCallback();

    this.overflowMenu?.addEventListener('pointerleave', () => this.#debouncedDeactivate(), {
      signal: this.#abortController.signal,
    });
    document.addEventListener('keyup', this.#handleDocumentKeyUp, {
      signal: this.#abortController.signal,
    });
    document.addEventListener('pointerdown', this.#handleDocumentPointerDown, {
      signal: this.#abortController.signal,
    });
    this.addEventListener('click', this.#onFeaturedSliderArrowClick, {
      signal: this.#abortController.signal,
    });
    this.addEventListener('scroll', this.#onFeaturedSliderScroll, {
      signal: this.#abortController.signal,
      capture: true,
    });
    window.addEventListener('resize', this.#updateFeaturedContentControls, {
      signal: this.#abortController.signal,
    });

    onDocumentLoaded(this.#preloadImages);
    this.#updateFeaturedContentControls();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    clearTimeout(this.#featuredSliderUpdateTimeout);
  }

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * Time to allow for a closing animation between initiating a deactivation and actually deactivating the active item.
   * @returns {number}
   */
  get animationDelay() {
    const value = this.dataset.animationDelay;
    return value ? parseInt(value, 10) : 0;
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow menu is hovered
   * @returns {boolean}
   */
  get overflowHovered() {
    return this.refs.overflowMenu?.matches(':hover') ?? false;
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    this.#debouncedDeactivate.cancel();
    this.#debouncedActivateHandler.cancel();

    this.#debouncedActivateHandler(event);
  };

  /**
   * Toggle a menu item through click interaction.
   * First click opens the submenu, second click on links navigates.
   * @param {MouseEvent} event
   */
  toggle = (event) => {
    if (!(event.target instanceof Element)) return;

    const item = findMenuListItem(event.target);
    if (!item) return;

    const trigger = findMenuTrigger(item);
    if (!trigger) return;

    const isOverflowRoot = item.slot === 'more';
    const submenu = findSubmenu(trigger);

    if (!submenu && !isOverflowRoot) return;

    const isExpanded = trigger.ariaExpanded === 'true';

    if (isExpanded) {
      if (
        item.slot === 'more' ||
        trigger instanceof HTMLButtonElement ||
        !trigger.getAttribute('href') ||
        trigger.getAttribute('href') === '#'
      ) {
        event.preventDefault();
        this.#deactivate(trigger);
      }
      return;
    }

    event.preventDefault();
    this.#activateMenuItem(trigger);
  };

  /**
   * Activate the selected menu item with a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #activateHandler = (event) => {
    this.#debouncedDeactivate.cancel();

    this.dispatchEvent(new MegaMenuHoverEvent());

    this.removeAttribute('data-animating');

    if (!(event.target instanceof Element)) return;

    const trigger = findMenuTrigger(event.target);

    if (!trigger || trigger === this.#state.activeItem) return;

    this.#activateMenuItem(trigger);
  };

  /**
   * @param {HTMLElement} trigger
   */
  #activateMenuItem = (trigger) => {
    const listItem = findMenuListItem(trigger);
    const isOverflowSlot = listItem?.slot === 'overflow' || listItem?.slot === 'more';

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem && previouslyActiveItem !== trigger) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.dataset.overflowExpanded = isOverflowSlot.toString();
    this.#state.activeItem = trigger;
    this.ariaExpanded = 'true';
    trigger.ariaExpanded = 'true';

    let submenu = findSubmenu(trigger);
    let overflowMenuHeight = this.overflowMenu?.offsetHeight ?? 0;

    if (!submenu && isOverflowSlot) {
      submenu = this.overflowMenu;
    }

    const submenuHeight = submenu ? Math.max(submenu.offsetHeight, overflowMenuHeight) : 0;

    this.style.setProperty('--submenu-height', `${submenuHeight}px`);
    this.style.setProperty('--submenu-opacity', '1');
    this.#scheduleFeaturedContentControlUpdate();
  };

  #debouncedActivateHandler = debounce(this.#activateHandler, ACTIVATE_DELAY);

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    this.#debouncedActivateHandler.cancel();

    if (!(event.target instanceof Element)) return;

    const item = findMenuTrigger(event.target);

    // Make sure the item to be deactivated is still the active one. Ideally
    // we cancelled the debounce before the item was changed, but just in case.
    if (item === this.#state.activeItem) {
      this.#debouncedDeactivate();
    }
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;
    if (this.overflowHovered) return;

    this.style.setProperty('--submenu-height', '0px');
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';
    item.setAttribute('data-animating', '');

    setTimeout(() => {
      item.removeAttribute('data-animating');
    }, Math.max(0, this.animationDelay - 150)); // Start header transition 150ms before submenu finishes
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #debouncedDeactivate = debounce(this.#deactivate, DEACTIVATE_DELAY);

  /**
   * @param {KeyboardEvent} event
   */
  #handleDocumentKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#debouncedActivateHandler.cancel();
    this.#debouncedDeactivate.cancel();
    this.#deactivate();
  };

  /**
   * @param {PointerEvent} event
   */
  #handleDocumentPointerDown = (event) => {
    if (!(event.target instanceof Node)) return;
    if (this.contains(event.target)) return;
    if (!this.#state.activeItem) return;

    this.#debouncedActivateHandler.cancel();
    this.#debouncedDeactivate.cancel();
    this.#deactivate();
  };

  /**
   * Handle arrow button clicks for featured content sliders.
   * @param {MouseEvent} event
   */
  #onFeaturedSliderArrowClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const button = event.target.closest('[data-featured-content-arrow]');
    if (!(button instanceof HTMLButtonElement)) return;

    const listId = button.getAttribute('aria-controls');
    if (!listId) return;

    const list = this.querySelector(`#${listId}`);
    if (!(list instanceof HTMLElement)) return;

    const direction = button.dataset.featuredContentArrow === 'next' ? 1 : -1;
    let scrollAmount = Math.max(list.clientWidth * 0.9, 1);
    if (list.classList.contains('mega-menu__featured-content-list--slider')) {
      const firstItem = list.querySelector('.mega-menu__content-list-item');
      if (firstItem instanceof HTMLElement) {
        const listStyles = getComputedStyle(list);
        const gap = parseFloat(listStyles.columnGap || listStyles.gap || '0');
        scrollAmount = Math.max((firstItem.clientWidth + gap) * 2, 1);
      }
    }
    list.scrollBy({ left: direction * scrollAmount, behavior: 'smooth' });

    this.#scheduleFeaturedContentControlUpdate();
  };

  /**
   * Keep slider arrows in sync with scroll position.
   * @param {Event} event
   */
  #onFeaturedSliderScroll = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    if (!target.classList.contains('mega-menu__featured-content-list--slider')) return;

    this.#updateFeaturedControlsForList(target);
  };

  #scheduleFeaturedContentControlUpdate() {
    clearTimeout(this.#featuredSliderUpdateTimeout);
    this.#featuredSliderUpdateTimeout = setTimeout(() => {
      this.#updateFeaturedContentControls();
    }, 220);
  }

  #updateFeaturedContentControls = () => {
    const featuredLists = this.querySelectorAll('.mega-menu__featured-content-list--slider');

    for (const list of featuredLists) {
      if (!(list instanceof HTMLElement)) continue;
      this.#updateFeaturedControlsForList(list);
    }
  };

  /**
   * @param {HTMLElement} list
   */
  #updateFeaturedControlsForList(list) {
    const listId = list.getAttribute('id');
    if (!listId) return;

    const controls = this.querySelectorAll(`[data-featured-content-arrow][aria-controls="${listId}"]`);
    if (!controls.length) return;

    const maxScrollLeft = Math.max(list.scrollWidth - list.clientWidth, 0);
    const isScrollable = maxScrollLeft > 1;
    const atStart = list.scrollLeft <= 1;
    const atEnd = list.scrollLeft >= maxScrollLeft - 1;

    for (const control of controls) {
      if (!(control instanceof HTMLButtonElement)) continue;

      const isPreviousButton = control.dataset.featuredContentArrow === 'prev';
      control.hidden = !isScrollable;
      control.disabled = isPreviousButton ? atStart : atEnd;
    }
  }

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuTrigger(element) {
  if (!(element instanceof Element)) return null;

  const listItem = findMenuListItem(element);
  if (listItem?.slot === 'more') {
    const firstOverflowTrigger = listItem.parentElement?.querySelector('[slot="overflow"] [ref="menuitem"]');
    if (firstOverflowTrigger instanceof HTMLElement) return firstOverflowTrigger;
  }

  if (element.matches('[ref="menuitem"]')) {
    return element instanceof HTMLElement ? element : null;
  }

  const closestTrigger = element.closest('[ref="menuitem"]');
  if (closestTrigger instanceof HTMLElement) return closestTrigger;

  if (!listItem) return null;

  const trigger = listItem.querySelector('[ref="menuitem"]');
  return trigger instanceof HTMLElement ? trigger : null;
}

/**
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuListItem(element) {
  if (!(element instanceof Element)) return null;

  const item = element.closest('.menu-list__list-item');
  return item instanceof HTMLElement ? item : null;
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const parentItem = findMenuListItem(element ?? null);
  const submenu = parentItem?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
